import { useState, useEffect } from 'react'
import { SupabaseService } from '../services/supabase-service'
import type { Livro } from '../types/livro'


interface FiltrosLivros {
  assunto?: string
  idioma?: string
  status_entrega?: string
  data_inicio?: string
  data_fim?: string
}

// Interface removida - não utilizada

interface UseLivrosReturn {
  livros: Livro[]
  loading: boolean
  error: string | null
  count: number
  buscarLivros: (filtros?: FiltrosLivros, limite?: number, offset?: number) => Promise<void>
  buscarLivrosDisponiveis: (limite?: number, offset?: number) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * Hook para gerenciar livros do cliente
 * Baseado na estrutura real do Supabase
 */
export function useLivros(clienteId?: string | number): UseLivrosReturn {
  const [livros, setLivros] = useState<Livro[]>([])
  const [loading, setLoading] = useState(true) // Inicia como loading
  const [error, setError] = useState<string | null>(null)
  const [initialLoad, setInitialLoad] = useState(true) // Controla se é o primeiro carregamento
  const [count, setCount] = useState(0)

  // Buscar livros com filtros
  const buscarLivros = async (
    filtros: FiltrosLivros = {},
    limite = 20,
    offset = 0
  ) => {
    if (clienteId === undefined || clienteId === null || clienteId === '') {
      setLivros([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const resultado = await SupabaseService.buscarLivrosDoCliente(
        clienteId,
        filtros,
        limite,
        offset
      )

      if (resultado.error) {
        setError(resultado.error)
        console.error('❌ Erro ao buscar livros:', resultado.error)
      } else {
        setLivros(resultado.data)
        setCount(resultado.count)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      setError(errorMessage)
      console.error('❌ Erro inesperado ao buscar livros:', err)
    } finally {
      setLoading(false)
    }
  }

  // Buscar apenas livros disponíveis para download
  const buscarLivrosDisponiveis = async (limite = 20, offset = 0) => {
    if (clienteId === undefined || clienteId === null || clienteId === '') {
      setLivros([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Delay mínimo para loading suave (apenas no carregamento inicial)
      if (initialLoad) {
        await new Promise(resolve => setTimeout(resolve, 300))
      }

      const resultado = await SupabaseService.buscarLivrosDisponiveis(
        clienteId,
        limite,
        offset
      )

      if (resultado.error) {
        setError(resultado.error)
        console.error('❌ Erro ao buscar livros disponíveis:', resultado.error)
      } else {
        // Segurança: filtrar client-side apenas entregues quando buscarLivrosDisponiveis for chamado
        setLivros(resultado.data)
        setCount(resultado.count)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      setError(errorMessage)
      console.error('❌ Erro inesperado ao buscar livros disponíveis:', err)
    } finally {
      setLoading(false)
      setInitialLoad(false) // Marca que o primeiro carregamento foi concluído
    }
  }


  // Carregar dados iniciais
  useEffect(() => {
    if (clienteId !== undefined && clienteId !== null && clienteId !== '') {
      buscarLivros()
    } else {
      setLoading(false)
    }
  }, [clienteId])

  const refetch = async () => {
    await buscarLivros()
  }

  return {
    livros,
    loading,
    error,
    count,
    buscarLivros,
    buscarLivrosDisponiveis,
    refetch
  }
}
